<template>
  <v-container class="pa-4">
    <v-card class="mx-auto" max-width="800" elevation="8">
      <!-- Header com gradiente -->
      <v-card-title class="white--text pa-6" :style="{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }">
        <div class="d-flex align-center justify-space-between w-100">
          <div>
            <h2 class="mb-1">Boleto #{{ String(bill.id).padStart(6, '0') }}</h2>
            <p class="mb-0 white--text" style="opacity: 0.9;">
              {{ setDateFormat(bill.created_at) }} às {{ setTimeFormat(bill.created_at) }}
            </p>
          </div>
          <v-chip :class="setClass(bill.status)" dark large>
            <v-icon left small>{{ getStatusIcon(bill.status) }}</v-icon>
            {{ bill.status ? $t(`status.${bill.status}`) : 'Processando' }}
          </v-chip>
        </div>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Informações básicas -->
        <v-layout row wrap class="mb-4">
          <v-flex xs12 md6 class="pa-2">
            <v-card outlined class="pa-4 fill-height">
              <h3 class="mb-3 primary--text">
                <v-icon color="primary" class="mr-2">account_circle</v-icon>
                Informações do Pagador
              </h3>
              <div class="mb-2">
                <span class="font-weight-medium">Nome:</span>
                <span class="ml-2">{{ bill.payer_name || 'Não informado' }}</span>
              </div>
              <div>
                <span class="font-weight-medium">Nosso Número:</span>
                <span class="ml-2">{{ bill.bill_number || 'Em processamento' }}</span>
              </div>
            </v-card>
          </v-flex>
          <v-flex xs12 md6 class="pa-2">
            <v-card outlined class="pa-4 fill-height">
              <h3 class="mb-3 success--text">
                <v-icon color="success" class="mr-2">attach_money</v-icon>
                Valor do Boleto
              </h3>
              <div class="display-1 success--text font-weight-bold">
                {{ $n((setAmount(bill.amount)), 'currency') }}
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Linha Digitável -->
        <v-card v-if="bill.digitable_line" outlined class="mb-4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">format_list_numbered</v-icon>
            Linha Digitável
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center">
              <v-text-field
                :value="bill.digitable_line"
                readonly
                outlined
                dense
                hide-details
                class="codigo-field flex-grow-1"
                ref="linhaDigitavel"
              ></v-text-field>
              <v-btn 
                color="primary"
                class="ml-3"
                @click="copyToClipboard(bill.digitable_line, 'Linha digitável')"
              >
                <v-icon left>file_copy</v-icon>
                Copiar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Dados PIX -->
        <v-card v-if="hasPix" outlined class="mb-4">
          <v-card-title class="pb-2">
            <v-icon color="orange" class="mr-2">qr_code</v-icon>
            Pagamento via PIX
          </v-card-title>
          <v-card-text>
            <!-- PIX TXID -->
            <div v-if="bill.pix_txid" class="mb-4">
              <h4 class="mb-2">PIX TXID</h4>
              <div class="d-flex align-center">
                <v-text-field
                  :value="bill.pix_txid"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="codigo-field flex-grow-1"
                  ref="pixTxid"
                ></v-text-field>
                <v-btn 
                  color="orange"
                  class="ml-3"
                  @click="copyToClipboard(bill.pix_txid, 'PIX TXID')"
                >
                  <v-icon left>file_copy</v-icon>
                  Copiar
                </v-btn>
              </div>
            </div>

            <!-- PIX QR Code (Copia e Cola) -->
            <div v-if="bill.pix_qr_code" class="mb-4">
              <h4 class="mb-2">PIX Copia e Cola</h4>
              <div class="d-flex align-start">
                <v-textarea
                  :value="truncatePixCode(bill.pix_qr_code)"
                  readonly
                  outlined
                  rows="4"
                  hide-details
                  class="codigo-field flex-grow-1"
                  ref="pixQrCode"
                ></v-textarea>
                <v-btn 
                  color="orange"
                  class="ml-3 mt-1"
                  @click="copyToClipboard(bill.pix_qr_code, 'PIX Copia e Cola')"
                >
                  <v-icon left>file_copy</v-icon>
                  Copiar
                </v-btn>
              </div>
              <v-alert type="info" text dense class="mt-2">
                <small>
                  <v-icon small class="mr-1">info</v-icon>
                  Abra o app do seu banco, vá em PIX → Pagar → Colar código e cole este código
                </small>
              </v-alert>
            </div>
          </v-card-text>
        </v-card>
        
        <!-- Descrição personalizada -->
        <v-card v-if="bill.description" outlined class="mb-4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">note</v-icon>
            Descrição
          </v-card-title>
          <v-card-text>
            <p class="mb-0">{{ bill.description }}</p>
          </v-card-text>
        </v-card>

        <!-- Status de retorno (apenas se diferente da descrição personalizada) -->
        <v-card v-if="bill.return_description && bill.return_description !== bill.description" outlined>
          <v-card-title class="pb-2">
            <v-icon color="info" class="mr-2">info</v-icon>
            Status
          </v-card-title>
          <v-card-text>
            <p class="mb-0">{{ bill.return_description }}</p>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-spacer></v-spacer>
        
        <!-- Botão de Download PDF -->
        <v-btn
          v-if="canDownload"
          color="success"
          large
          @click="downloadPdf"
          :loading="downloadingPdf"
          class="mr-3"
        >
          <v-icon left>get_app</v-icon>
          Download PDF
        </v-btn>
        
        <v-tooltip v-else bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-on="on"
              v-bind="attrs"
              color="grey"
              large
              disabled
              class="mr-3"
            >
              <v-icon left>get_app</v-icon>
              PDF indisponível
            </v-btn>
          </template>
          <span>O PDF ficará disponível assim que o registro do boleto estiver completo em alguns instantes.</span>
        </v-tooltip>
        
        <v-btn
          color="primary"
          large
          @click.stop="goTo('boleto')"
        >
          <v-icon left>arrow_back</v-icon>
          Voltar para Lista
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ShowBillPayment',
  data () {
    return {
      downloadingPdf: false
    }
  },
  computed: {
    ...mapGetters({
      bill: 'bills/showBillPayment',
      primaryColor: "system/primaryColor",
      secondaryColor: "system/secondaryColor",
    }),
    hasPix() {
      return this.bill && (this.bill.pix_qr_code || this.bill.pix_txid);
    },
    canDownload() {
      return this.bill && 
             this.bill.bill_number && 
             this.bill.status !== 'processing' && 
             this.bill.digitable_line;
    }
  },
  created() {
    this.$store.dispatch('bills/showBillPayment', parseInt(this.$route.params.id));
    // Fazer consulta automática do status para atualizar dados em tempo real
    this.checkBillStatus();
  },
  components: {

  },
  methods: {
    setAmount(amount){
      return amount // O valor já está no formato correto (100.0 = R$ 100,00)
    },
    setDateFormat(date) {
      if (date === null) {
        return ''
      } else {
        return new Date(date).toLocaleDateString("pt-BR")
      }
    },
    setTimeFormat(date) {
      if (date === null) {
        return ''
      } else {
        return new Date(date).toLocaleTimeString("pt-BR")
      }
    },
    goTo(route) {
      this.$router.push({ name: route });
    },
    setClass(status) {
      return `invoice ${status}`;
    },
    async copyToClipboard(text, type) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback para browsers antigos
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        
        // Mostrar notificação de sucesso
        this.$toast.success(`${type} copiado!`);
      } catch (err) {
        console.error('Erro ao copiar:', err);
        this.$toast.error(`Erro ao copiar ${type}`);
      }
    },
    getStatusIcon(status) {
      const icons = {
        'completed': 'check_circle',
        'waiting': 'schedule',
        'processing': 'autorenew',
        'cancelled': 'cancel'
      };
      return icons[status] || 'help';
    },
    truncatePixCode(code) {
      if (!code) return '';
      const maxLength = 30; // Define o comprimento máximo para truncamento
      if (code.length <= maxLength) {
        return code;
      }
      return code.substring(0, maxLength) + '...';
    },
    
    async checkBillStatus() {
      try {
        // Verificar se o boleto está em processamento e fazer consulta automática
        if (this.bill && (this.bill.status === 'processing' || this.bill.status === 'waiting')) {
          await this.$store.dispatch('bills/checkBillStatus', {
            id: this.bill.id,
            check_status: true
          });
        }
      } catch (error) {
        console.error('Erro ao verificar status do boleto:', error);
      }
    },
    
    async downloadPdf() {
      try {
        this.downloadingPdf = true;
        
        // Use store dispatch to download PDF
        const result = await this.$store.dispatch('bills/downloadPdf', this.bill.id);
        
        if (result && result.success) {
          // Create blob and download
          const blob = new Blob([result.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `boleto-${this.bill.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          // Show success message
          this.$toast ? this.$toast.success('PDF baixado com sucesso!') : console.log('PDF baixado com sucesso!');
        } else {
          this.$toast ? this.$toast.error('Erro ao baixar o PDF. Tente novamente.') : console.error('Erro ao baixar o PDF');
        }
      } catch (error) {
        console.error('Erro ao baixar PDF:', error);
        this.$toast ? this.$toast.error('Erro ao baixar o PDF. Tente novamente.') : console.error('Erro ao baixar PDF');
      } finally {
        this.downloadingPdf = false;
      }
    }
  },
  watch: {

  },
};
</script>
<style scoped>
  .v-chip.invoice.cancelled    { background-color: #B71C1C !important; }
  .v-chip.invoice.completed    { background-color: #1B5E20 !important; }
  .v-chip.invoice.waiting      { background-color: #EF6C00 !important; }
  .v-chip.invoice.processing   { background-color: var(--primary-color, #1976D2) !important; }
  
  .codigo-field {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  }
  
  .codigo-field .v-input__control {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 14px;
  }
  
  .codigo-field .v-text-field__details {
    display: none;
  }
  
  .codigo-field textarea {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 12px;
    line-height: 1.4;
    word-break: break-all;
  }

  .codigo-field input {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 13px;
    word-spacing: 2px;
  }

  /* Melhorar aparência dos cards */
  .v-card.v-card--outlined {
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
  }

  .v-card.v-card--outlined:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  /* Estilo para alertas */
  .v-alert.v-alert--text {
    background-color: rgba(33, 150, 243, 0.1) !important;
    border-color: #2196F3 !important;
  }

  /* Responsividade para botões de copiar */
  @media (max-width: 600px) {
    .d-flex.align-center .v-btn,
    .d-flex.align-start .v-btn {
      min-width: auto !important;
      padding: 0 12px !important;
    }
    
    .d-flex.align-center .v-btn .v-icon,
    .d-flex.align-start .v-btn .v-icon {
      margin-right: 4px !important;
    }
  }
</style>
